import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  WalletCards,
} from "lucide-react";
import * as XLSX from "xlsx";
import { getGroupInvoiceReport } from "../../services/invoiceService";
import "./InvoiceReport.css";
import PaymentModal from "../../components/PaymentModal/PaymentModal";

const PERIOD_OPTIONS = [
  { value: "last30Days", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "last3Months", label: "Last 3 Months" },
  { value: "last6Months", label: "Last 6 Months" },
  { value: "last1Year", label: "Last 1 Year" },
  { value: "custom", label: "Custom" },
];

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addMonths = (date, months) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const getDateRangeForPeriod = (period) => {
  const today = new Date();

  if (period === "last30Days") {
    const from = new Date();
    from.setDate(today.getDate() - 30);
    return {
      FromDate: formatDate(from),
      ToDate: formatDate(today),
    };
  }

  if (period === "lastMonth") {
    const previousMonth = addMonths(today, -1);
    return {
      FromDate: formatDate(startOfMonth(previousMonth)),
      ToDate: formatDate(endOfMonth(previousMonth)),
    };
  }

  if (period === "thisMonth") {
    return {
      FromDate: formatDate(startOfMonth(today)),
      ToDate: formatDate(today),
    };
  }

  if (period === "last3Months") {
    return {
      FromDate: formatDate(addMonths(today, -3)),
      ToDate: formatDate(today),
    };
  }

  if (period === "last6Months") {
    return {
      FromDate: formatDate(addMonths(today, -6)),
      ToDate: formatDate(today),
    };
  }

  if (period === "last1Year") {
    return {
      FromDate: formatDate(addMonths(today, -12)),
      ToDate: formatDate(today),
    };
  }

  return { FromDate: "", ToDate: "" };
};

const getUniqueValues = (data, key) => {
  const values = data
    .map((item) => item[key])
    .filter((value) => value !== undefined && value !== null && value !== "");
  return [...new Set(values)].sort();
};

const cleanValue = (val) => String(val || "").trim();

const parseAmount = (val) => {
  const parsed = parseFloat(String(val || "0").replace(/[^0-9.-]+/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (val) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeStatus = (status) => String(status || "PENDING").trim().toUpperCase();

const getStatusClass = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === "PAID" || normalized === "SUCCESS") return "paid";
  if (normalized === "FAILED" || normalized === "CANCELLED") return "failed";
  return "pending";
};

const InvoiceReport = () => {
  const itemsPerPage = 12;
  const defaultRange = useMemo(() => getDateRangeForPeriod("last30Days"), []);
  const [period, setPeriod] = useState("last30Days");
  const [dateRange, setDateRange] = useState(defaultRange);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    courseName: "",
    batchName: "",
    paymentStatus: "",
    search: "",
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const loadReport = useCallback(async (range) => {
    if (!range.FromDate || !range.ToDate) return;
    try {
      setLoading(true);
      setError("");
      console.log("Loading invoice report for range", range);
      const res = await getGroupInvoiceReport({
        FromDate: range.FromDate,
        ToDate: range.ToDate,
      });
      console.log("Invoice report loaded", res);
      setInvoices(Array.isArray(res) ? res : []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Invoice report load failed", err);
      setInvoices([]);
      setError(err?.message || err || "Unable to load invoice report");
    } finally {
      setLoading(false);
    }
  }, []);

  // Combined Effect to handle both Period and Manual Date changes
  useEffect(() => {
    if (period !== "custom") {
      const nextRange = getDateRangeForPeriod(period);
      setDateRange(nextRange);
      setFilters((prev) => ({ ...prev, courseName: "", batchName: "", paymentStatus: "" }));
      setCurrentPage(1);
      loadReport(nextRange);
    } else if (dateRange.FromDate && dateRange.ToDate) {
      // Immediate load for custom date changes
      loadReport(dateRange);
    }
  }, [period, dateRange.FromDate, dateRange.ToDate, loadReport]);

  const filterOptions = useMemo(() => ({
    courses: getUniqueValues(invoices, "CourseName"),
    batches: getUniqueValues(invoices, "BatchName"),
    statuses: getUniqueValues(invoices.map((item) => ({
      ...item,
      PaymentStatus: normalizeStatus(item.PaymentStatus || item.Status),
    })), "PaymentStatus"),
  }), [invoices]);

  const filteredInvoices = useMemo(() => {
    const query = filters.search.trim().toLowerCase();

    return invoices.filter((invoice) => {
      const status = normalizeStatus(invoice.PaymentStatus || invoice.Status);
      const matchesCourse = !filters.courseName || cleanValue(invoice.CourseName) === filters.courseName;
      const matchesBatch = !filters.batchName || cleanValue(invoice.BatchName) === filters.batchName;
      const matchesStatus = !filters.paymentStatus || status === filters.paymentStatus;
      const matchesSearch = !query || [
        invoice.InvoiceNumber,
        invoice.StudentName,
        invoice.ParentName,
        invoice.PhoneNumber,
        invoice.BranchName,
      ].some((value) => String(value || "").toLowerCase().includes(query));

      return matchesCourse && matchesBatch && matchesStatus && matchesSearch;
    });
  }, [filters, invoices]);

  const summary = useMemo(() => {
    return filteredInvoices.reduce((acc, invoice) => {
      const amount = parseAmount(invoice.Amount);
      const status = normalizeStatus(invoice.PaymentStatus || invoice.Status);
      acc.total += amount;
      acc.count += 1;
      if (status === "PAID" || status === "SUCCESS") {
        acc.paid += amount;
        acc.paidCount += 1;
      } else {
        acc.pending += amount;
        acc.pendingCount += 1;
      }
      return acc;
    }, { total: 0, paid: 0, pending: 0, count: 0, paidCount: 0, pendingCount: 0 });
  }, [filteredInvoices]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleDateChange = (name, value) => {
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomApply = () => {
    if (!dateRange.FromDate || !dateRange.ToDate) {
      setError("Please select both From Date and To Date");
      return;
    }

    if (dateRange.FromDate > dateRange.ToDate) {
      setError("From Date cannot be later than To Date");
      return;
    }

    loadReport(dateRange);
  };

  const resetDataFilters = () => {
    setFilters({ courseName: "", batchName: "", paymentStatus: "", search: "" });
    setCurrentPage(1);
  };

  const hasBatchData = filterOptions.batches.length > 0;
  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / itemsPerPage));
  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredInvoices.slice(start, start + itemsPerPage);
  }, [currentPage, filteredInvoices, itemsPerPage]);

  const downloadExcel = () => {
    const rows = filteredInvoices.map((invoice) => ({
      "Invoice ID": invoice.InvoiceId,
      "Invoice Number": invoice.InvoiceNumber,
      "Invoice Date": formatDisplayDate(invoice.InvoiceDate),
      "Due Date": formatDisplayDate(invoice.DueDate),
      "Student Name": invoice.StudentName,
      "Parent Name": invoice.ParentName,
      Phone: invoice.PhoneNumber,
      Email: invoice.Email,
      Branch: invoice.BranchName,
      ...(hasBatchData ? { Batch: invoice.BatchName } : {}),
      Course: invoice.CourseName,
      Amount: parseAmount(invoice.Amount),
      "Payment Status": normalizeStatus(invoice.PaymentStatus || invoice.Status),
      Items: invoice.Items,
      "Bank Name": invoice.BankName,
      IFSC: invoice.IFSC,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice Report");
    XLSX.writeFile(workbook, `invoice-report-${dateRange.FromDate}-to-${dateRange.ToDate}.xlsx`);
  };

  return (
    <div className="invoice-report-page">
      <section className="invoice-toolbar">
        <div className="filter-field">
          <label>Period</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label>From Date</label>
          <input
            type="date"
            value={dateRange.FromDate}
            onChange={(e) => {
              setPeriod("custom");
              handleDateChange("FromDate", e.target.value);
            }}
          />
        </div>
        <div className="filter-field">
          <label>To Date</label>
          <input
            type="date"
            value={dateRange.ToDate}
            onChange={(e) => {
              setPeriod("custom");
              handleDateChange("ToDate", e.target.value);
            }}
          />
        </div>

        <div className="toolbar-spacer" />

        <button
          className="invoice-icon-btn"
          onClick={downloadExcel}
          disabled={!filteredInvoices.length}
          title="Download Excel"
          aria-label="Download Excel"
        >
          <Download size={18} />
        </button>
      </section>

      <section className="invoice-data-filters">
        <div className="search-field">
          <Search size={16} />
          <input
            type="search"
            placeholder="Search invoice, student, parent, phone"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </div>

        <select value={filters.courseName} onChange={(e) => handleFilterChange("courseName", e.target.value)}>
          <option value="">All Courses</option>
          {filterOptions.courses.map((course) => (
            <option key={course} value={course}>{course}</option>
          ))}
        </select>

        {hasBatchData && (
          <select value={filters.batchName} onChange={(e) => handleFilterChange("batchName", e.target.value)}>
            <option value="">All Batches</option>
            {filterOptions.batches.map((batch) => (
              <option key={batch} value={batch}>{batch}</option>
            ))}
          </select>
        )}

        <select value={filters.paymentStatus} onChange={(e) => handleFilterChange("paymentStatus", e.target.value)}>
          <option value="">All Payment Status</option>
          {filterOptions.statuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>

        <button className="invoice-secondary-btn" onClick={resetDataFilters}>Clear</button>
      </section>

      {error && (
        <div className="invoice-alert">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <section className="invoice-summary-grid">
        <div className="invoice-summary-card total">
          <div>
            <span>Total Invoices</span>
            <strong>{summary.count}</strong>
          </div>
          <FileText size={28} />
        </div>
        <div className="invoice-summary-card paid">
          <div>
            <span>Paid Amount</span>
            <strong>{formatCurrency(summary.paid)}</strong>
          </div>
          <CheckCircle2 size={28} />
        </div>
        <div className="invoice-summary-card pending">
          <div>
            <span>Pending Amount</span>
            <strong>{formatCurrency(summary.pending)}</strong>
          </div>
          <WalletCards size={28} />
        </div>
        <div className="invoice-summary-card range">
          <div>
            <span>Date Range</span>
            <strong>{formatDisplayDate(dateRange.FromDate)} - {formatDisplayDate(dateRange.ToDate)}</strong>
          </div>
          <Calendar size={28} />
        </div>
      </section>

      <section className="invoice-results-head">
        <div>
          <h3>Invoices</h3>
          <p>{filteredInvoices.length} of {invoices.length} records shown</p>
        </div>
        {loading && <span className="loading-chip"><Loader2 size={14} className="spin" /> Loading</span>}
      </section>

      {!loading && !filteredInvoices.length ? (
        <div className="invoice-empty-state">
          <FileText size={34} />
          <h3>No invoices found</h3>
          <p>Try a wider date range or clear the data filters.</p>
        </div>
      ) : (
        <section className="invoice-tile-grid">
          {paginatedInvoices.map((invoice) => {
            const status = normalizeStatus(invoice.PaymentStatus || invoice.Status);
            return (
              <article className="invoice-tile" key={`${invoice.InvoiceId}-${invoice.InvoiceNumber}`}>
                <div className="invoice-tile-top">
                  <div>
                    <span className="invoice-number">{invoice.InvoiceNumber || `Invoice ${invoice.InvoiceId}`}</span>
                    <p>{formatDisplayDate(invoice.InvoiceDate)}</p>
                  </div>
                  <span className={`payment-pill ${getStatusClass(status)}`}>{status}</span>
                </div>

                <div className="invoice-student-block">
                  <div className="student-avatar">{String(invoice.StudentName || "S").charAt(0)}</div>
                  <div>
                    <h4>{invoice.StudentName || "-"}</h4>
                    <p>{invoice.ParentName || "-"} - {invoice.PhoneNumber || "-"}</p>
                  </div>
                </div>

                <div className="invoice-meta-grid">
                  <div>
                    <span>Course</span>
                    <strong>{invoice.CourseName || "-"}</strong>
                  </div>
                  {hasBatchData && (
                    <div>
                      <span>Batch</span>
                      <strong>{cleanValue(invoice.BatchName) || "-"}</strong>
                    </div>
                  )}
                  <div>
                    <span>Branch</span>
                    <strong>{invoice.BranchName || "-"}</strong>
                  </div>
                  <div>
                    <span>Due Date</span>
                    <strong>{formatDisplayDate(invoice.DueDate)}</strong>
                  </div>
                </div>

                <div className="invoice-tile-bottom">
                  <strong>{formatCurrency(invoice.Amount)}</strong>
                </div>

                {status !== "PAID" && status !== "SUCCESS" && (
                  <button
                    className="invoice-primary-btn"
                    style={{ marginTop: '12px', width: '100%', borderRadius: '8px' }}
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setShowPaymentModal(true);
                    }}
                  >
                    Mark as Paid
                  </button>
                )}
              </article>
            );
          })}
        </section>
      )}

      {!loading && filteredInvoices.length > itemsPerPage && (
        <section className="invoice-pagination">
          <span>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, filteredInvoices.length)} of {filteredInvoices.length}
          </span>
          <div>
            <button
              className="invoice-secondary-btn"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <strong>{currentPage} / {totalPages}</strong>
            <button
              className="invoice-secondary-btn"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </section>
      )}

      {showPaymentModal && selectedInvoice && (
        <PaymentModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
          }}
          onSuccess={() => loadReport(dateRange)}
        />
      )}
    </div>
  );
};

export default InvoiceReport;
